/**
 * Top-level onboarding wizard. Drives the user through the steps
 * returned by GET /api/onboarding/state. Step order:
 *
 *  Firm-owner (tenant flow):
 *    email_verification  (required, 6-digit OTP)
 *    tour                (optional, modal walkthrough)
 *
 *  Invited team member (user flow):
 *    profile             (required: phone, photo URL, Bar Council #)
 *    preferences         (optional: notification toggles)
 *    tour                (optional)
 *
 * Persisted on the server, fully resumable across logins.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import EmailVerifyStep from "./EmailVerifyStep";
import ProfileStep from "./ProfileStep";
import PrefsStep from "./PrefsStep";
import TourStep from "./TourStep";
import { Scale, Check } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

const STEP_ORDER = {
    tenant: ["email_verification", "tour"],
    user:   ["profile", "preferences", "tour"],
};

const STEP_LABEL = {
    email_verification: "Verify email",
    profile:            "Your profile",
    preferences:        "Notifications",
    tour:               "Quick tour",
};

export default function OnboardingWizard() {
    const navigate = useNavigate();
    const [obState, setObState] = useState(null);
    const [busy, setBusy] = useState(false);
    const branding = useBranding();
    const logoH = Math.max(20, Math.min(36, Number(branding.logo_display_height) || 24));
    const showIcon = (branding.logo_layout || "icon_and_text") !== "text_only";
    const showText = (branding.logo_layout || "icon_and_text") !== "icon_only";

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/onboarding/state");
            setObState(data);
            if (data.flow === "none" || data.stage === "complete") {
                navigate("/app", { replace: true });
            }
        } catch (e) { toast.error(apiError(e)); }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    const finish = async () => {
        setBusy(true);
        try {
            await api.post("/onboarding/complete-tour");
            toast.success("You're all set — welcome to Law OS");
            navigate("/app", { replace: true });
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const skipCurrent = async () => {
        if (!obState) return;
        setBusy(true);
        try {
            await api.post("/onboarding/skip-step", { stage: obState.stage });
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const steps = useMemo(() => obState ? (STEP_ORDER[obState.flow] || []) : [], [obState]);
    const currentIdx = obState ? steps.indexOf(obState.stage) : -1;
    const isRequired = obState ? obState.required_stages?.includes(obState.stage) : false;

    if (!obState) {
        return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">Loading…</div>;
    }

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="onboarding-wizard">
            <header className="border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {showIcon && (branding.logo_src ? (
                            <img src={branding.logo_src} alt={branding.app_name}
                                style={{ height: logoH, width: "auto", maxWidth: logoH * 2.5 }}
                                className="object-contain"
                                onError={(e) => { e.target.style.display = "none"; }}/>
                        ) : (
                            <Scale className="w-5 h-5 text-primary" strokeWidth={1.5}/>
                        ))}
                        {showText && <span className="font-semibold tracking-[0.16em] uppercase text-sm">{branding.app_name || "Law OS"}</span>}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="onboarding-progress-label">
                        Step {Math.max(1, currentIdx + 1)} of {steps.length}
                    </div>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-6 py-8">
                <Stepper steps={steps} currentIdx={currentIdx}/>

                <div className="mt-8 border border-border bg-card p-6 sm:p-8" data-testid={`onboarding-step-${obState.stage}`}>
                    {obState.stage === "email_verification" && <EmailVerifyStep state={obState} onAdvance={load}/>}
                    {obState.stage === "profile" && <ProfileStep state={obState} onAdvance={load}/>}
                    {obState.stage === "preferences" && <PrefsStep state={obState} onAdvance={load}/>}
                    {obState.stage === "tour" && <TourStep onFinish={finish} busy={busy}/>}
                </div>

                {!isRequired && obState.stage !== "tour" && (
                    <div className="text-center mt-4">
                        <button onClick={skipCurrent} disabled={busy} data-testid="onboarding-skip"
                            className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
                            Skip this step
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Stepper({ steps, currentIdx }) {
    return (
        <div className="flex items-center gap-2 sm:gap-4" data-testid="onboarding-stepper">
            {steps.map((s, idx) => {
                const done = idx < currentIdx;
                const active = idx === currentIdx;
                return (
                    <div key={s} className="flex-1 flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border ${
                            done ? "bg-primary text-primary-foreground border-primary" :
                            active ? "border-primary text-primary" :
                            "border-border text-muted-foreground"
                        }`} data-testid={`onboarding-stepper-${s}`}>
                            {done ? <Check className="w-3.5 h-3.5" strokeWidth={2}/> : idx + 1}
                        </div>
                        <div className={`text-[10px] uppercase tracking-[0.16em] hidden sm:block ${active ? "text-foreground" : done ? "text-muted-foreground" : "text-muted-foreground"}`}>
                            {STEP_LABEL[s] || s}
                        </div>
                        {idx < steps.length - 1 && <div className={`flex-1 h-px ${done ? "bg-primary" : "bg-border"}`}/>}
                    </div>
                );
            })}
        </div>
    );
}
